import React, { useState } from 'react'
import { CoreTeam, CoreTeamStatsMetaProps, Stream } from '../data/types'
import { ChevronDownIcon, ChevronUpIcon } from '../icons'

type Props = {
    meta: CoreTeamStatsMetaProps
    data: CoreTeam[]
    streamFilter?: Stream | ''
    tierFilter?: number | ''
    statusFilter?: 'approved' | 'pending' | ''
    activityStatusFilter?: 'active' | 'on hold' | ''
    duplicationsFilter?: boolean | null
    searchTerm?: string
}

type StatItem = {
    label: string
    count: number
    percentage: number
}

export const CoreTeamStats = (props: Props) => {
    const { meta, data, streamFilter, tierFilter, statusFilter, activityStatusFilter, duplicationsFilter, searchTerm } = props
    const [isExpanded, setIsExpanded] = useState(false)

    // Apply the same filters as CoreTeamTable
    // TODO Extract filtering logic to a shared utility function
    const filteredData = React.useMemo(() => {
        let filtered = data

        // Exclude reserved (tier 5) entries from stats
        filtered = filtered.filter(item => item.tier !== 5)

        // Apply search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase()
            filtered = filtered.filter(item => {
                const volunteerId = item.volunteer?.csid?.toString() || ''
                const fullName = `${item.volunteer?.first_name} ${item.volunteer?.last_name}`.toLowerCase()
                const role = item.role?.toLowerCase() || ''
                const streams = item.streams?.join(' ').toLowerCase() || ''
                const email = item.volunteer?.email?.toLowerCase() || ''
                const phone = item.volunteer?.phone?.toLowerCase() || ''

                return volunteerId.includes(term) ||
                       fullName.includes(term) ||
                       role.includes(term) ||
                       streams.includes(term) ||
                       email.includes(term) ||
                       phone.includes(term)
            })
        }

        // Apply stream filter
        if (streamFilter) {
            filtered = filtered.filter(item => item.streams?.includes(streamFilter as any))
        }

        // Apply tier filter
        if (tierFilter !== '' && tierFilter !== undefined) {
            filtered = filtered.filter(item => item.tier === tierFilter)
        }

        // Apply status filter
        if (statusFilter === 'approved') {
            filtered = filtered.filter(item => item.approved === true)
        } else if (statusFilter === 'pending') {
            filtered = filtered.filter(item => item.approved === false)
        }

        // Apply activity status filter
        if (activityStatusFilter === 'active') {
            filtered = filtered.filter(item => item.status === 'active')
        } else if (activityStatusFilter === 'on hold') {
            filtered = filtered.filter(item => item.status === 'on hold')
        }

        // Apply duplications filter
        if (duplicationsFilter !== null && duplicationsFilter !== undefined) {
            filtered = filtered.filter(item => {
                // Use duplicate_check_allocations_count which excludes allocations from events with include_in_duplicate_checks = false
                const allocationsCount = item.volunteer?.duplicate_check_allocations_count ?? 0
                const coreTeamsCount = item.volunteer?.core_teams_count ?? 0
                // Use filtered lead counts that exclude positions from events with include_in_duplicate_checks = false
                const leadPositionsCount = (
                    (item.volunteer?.duplicate_check_event_leads_count ?? 0) +
                    (item.volunteer?.duplicate_check_areas_as_lead_count ?? 0) +
                    (item.volunteer?.duplicate_check_areas_as_deputy_lead_count ?? 0) +
                    (item.volunteer?.duplicate_check_shifts_as_lead_count ?? 0) +
                    (item.volunteer?.duplicate_check_shifts_as_deputy_lead_count ?? 0)
                )
                const totalCount = allocationsCount + coreTeamsCount + leadPositionsCount
                const hasDuplications = totalCount > 1

                return duplicationsFilter ? hasDuplications : !hasDuplications
            })
        }

        return filtered
    }, [data, searchTerm, streamFilter, tierFilter, statusFilter, activityStatusFilter, duplicationsFilter])

    // Calculate gender statistics
    const genderStats = React.useMemo(() => {
        const stats: Record<string, number> = {}
        let total = 0

        filteredData.forEach(item => {
            if (item.volunteer?.gender) {
                const gender = item.volunteer.gender
                stats[gender] = (stats[gender] || 0) + 1
                total++
            }
        })

        return Object.entries(stats).map(([label, count]): StatItem => ({
            label: label.charAt(0).toUpperCase() + label.slice(1),
            count,
            percentage: total > 0 ? (count / total) * 100 : 0
        }))
    }, [filteredData])

    // Calculate region statistics (London vs Not London)
    const regionStats = React.useMemo(() => {
        let londonCount = 0
        let notLondonCount = 0

        filteredData.forEach(item => {
            const region = item.volunteer?.region
            if (region && region.toLowerCase().includes('london')) {
                londonCount++
            } else {
                // Treat empty/null region as "Not London"
                notLondonCount++
            }
        })

        const total = londonCount + notLondonCount

        return [
            {
                label: meta.londonLabel,
                count: londonCount,
                percentage: total > 0 ? (londonCount / total) * 100 : 0
            },
            {
                label: meta.notLondonLabel,
                count: notLondonCount,
                percentage: total > 0 ? (notLondonCount / total) * 100 : 0
            }
        ].filter(stat => stat.count > 0)
    }, [filteredData, meta.londonLabel, meta.notLondonLabel])

    // Calculate family origin statistics
    const familyOriginStats = React.useMemo(() => {
        const stats: Record<string, number> = {}
        let total = 0

        filteredData.forEach(item => {
            const origin = item.volunteer?.family_origin || meta.otherLabel
            stats[origin] = (stats[origin] || 0) + 1
            total++
        })

        return Object.entries(stats)
            .map(([label, count]): StatItem => ({
                label,
                count,
                percentage: total > 0 ? (count / total) * 100 : 0
            }))
            .sort((a, b) => b.count - a.count) // Sort by count descending
    }, [filteredData])

    if (filteredData.length === 0) {
        return null
    }

    return (
        <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        {meta.statsTitle}
                    </h2>
                    <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                        {meta.excludedReservedNote}
                    </span>
                </div>
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                    {isExpanded ? meta.hideStatsButton : meta.showStatsButton}
                    {isExpanded ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                </button>
            </div>
            {isExpanded && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Gender Statistics */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                        {meta.genderTitle}
                    </h3>
                    <div className="space-y-2">
                        {genderStats.map((stat, index) => (
                            <div key={index} className="flex items-center justify-between">
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {stat.label}
                                </span>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {stat.count}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-500">
                                        ({stat.percentage.toFixed(1)}%)
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Region Statistics */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                        {meta.regionTitle}
                    </h3>
                    <div className="space-y-2">
                        {regionStats.map((stat, index) => (
                            <div key={index} className="flex items-center justify-between">
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {stat.label}
                                </span>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {stat.count}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-500">
                                        ({stat.percentage.toFixed(1)}%)
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Family Origin Statistics */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                        {meta.familyOriginTitle}
                    </h3>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                        {familyOriginStats.map((stat, index) => (
                            <div key={index} className="flex items-center justify-between">
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {stat.label}
                                </span>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {stat.count}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-500">
                                        ({stat.percentage.toFixed(1)}%)
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            )}
        </div>
    )
}
